
import React, { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Spin, Input, Pagination, Empty, Button, Modal } from 'antd'
import { COLORS } from 'theme'
import { SearchOutlined } from '@ant-design/icons'
import FilterIcon from 'components/icons/FilterIcon'
import ArrowDropDown from 'components/icons/ArrowDropDown'
import FilterButtonComponent from 'components/FilterButton'
import { FilterContainer, FilterPopup } from 'components/FilterButton/styles'
import Avatar from 'components/Avatar'
import StudentReportModal from '../StudentReport'
import NewStudentReport from '../FullStudentReport'
import { selectChartFilters } from '../../selectors'
import {
  SearchSection,
  ShowingText,
  Overlay,
  TableTop,
  CustomStyledTable,
  FilterSummary,
  ClearButton,
} from '../../Style/style'

const StudentsTable = ({
  loading,
  students = [],
  statusType,
  totalItems = 0,
  limit = 10,
  page = 0,
  onPageChange = () => {},
  filterValues = {},
  onClearFilters = () => {},
  assessmentId,
  exportStudentData,
  sendRemindersToStudents,
  resendInvitesToStudents,
  entityType = 'college',
  allowProctoring = false,
  assessmentType = '', 
  studentPage,
  setStudentPage,
  studentSearch,
  setStudentSearch,
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [showReportModal, setShowReportModal] = useState(false)
  const [sendingReminders, setSendingReminders] = useState(false)
  const [resendingInvites, setResendingInvites] = useState(false)
  const [studentForReport, setStudentForReport] = useState(null)
  
 

  const formatDateOnly = dateString => {
    if (!dateString) return ''
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '-' 
    const options = { day: '2-digit', month: 'short', year: 'numeric' }
    return date.toLocaleDateString('en-US', options)
  }

  
  const renderScore = text => (
    <div style={{ color: '#343E59', fontWeight: 'bold' }}>
      {typeof text === 'number' ? text.toFixed(2) : text || '-'}
    </div>
  )

  // Helper function to render percentage with consistent styling
  const renderPercentage = text => (
    <div style={{ color: '#343E59', fontWeight: 'bold' }}>
      {typeof text === 'number' ? `${text.toFixed(2)}%` : text || '-'}
    </div>
  )


  const renderProficiency = level => {
    
    let color = '#0FB680'; 
    
    
    if (level?.toLowerCase() === 'beginner') {
      color = '#F97066'; 
    }
    
    return (
      <div style={{ color, fontWeight: 'bold' }}>
        {level || '-'}
      </div>
    );
  }

  //console.log('Current assessment type:', assessmentType) 



  const paginatedStudents = useMemo(() => {
    const startIndex = page * limit;
    const endIndex = startIndex + limit;
    // The `students` prop is already filtered by the parent component.
    return students.slice(startIndex, endIndex);
  }, [students, page, limit]);

  //console.log(
  //   `StudentsTable received: status=${statusType}, students=`,
  //   students
  // )

  const handleSearch = e => {
    setStudentPage(1)
    setStudentSearch(e.target.value)
  
  }

  const handlePageChange = newPage => {
    setStudentPage(newPage)
  }

  const handleStudentClick = student => {
    setStudentForReport(student)
  }

  const handleExportData = () => {
    if (assessmentId) {
      exportStudentData(assessmentId, entityType)
    } else {
      console.error('Assessment ID is required for export')
    }
  }

  const handleSendReminders = async () => {
    if (!assessmentId) {
      console.error('Assessment ID is required to send reminders')
      return
    }

    setSendingReminders(true)
    try {
      await sendRemindersToStudents(assessmentId, entityType)
     
    } finally {
      setSendingReminders(false)
    }
  }

  const handleResendInvites = async () => {
    if (!assessmentId) {
      console.error('Assessment ID is required to resend invites')
      return
    }

    setResendingInvites(true)
    try {
      await resendInvitesToStudents(assessmentId, entityType)
    
    } finally {
      setResendingInvites(false)
    }
  }


  const baseColumns = [
    {
      title: 'STUDENT NAME',
      dataIndex: 'studentName',
      key: 'studentName',
      width: '16%',
      sorter: true,
      render: (text, record) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            color: '#3249D7',
            fontWeight: 600,
          }}
          onClick={() => handleStudentClick(record)}
        >
          {record.studentImgUrl && (
            <Avatar
              src={record.studentImgUrl}
              alt={text}
              size={32}
              style={{ marginRight: '10px' }}
            />
          )}
          <div>
            <div>{text}</div>
            {record.studentId && (
              <div style={{ fontSize: '12px', color: '#6B7280' }}>
                {record.studentId}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'CONTACT DETAILS',
      dataIndex: 'contactDetails',
      key: 'contactDetails',
      width: '16%',
      render: (_, record) => (
        <div>
          <div>{record?.primaryEmail}</div>
       
          {record?.contactInfo && (
            <div style={{ fontSize: '12px', color: '#6B7280' }}>
              {record?.contactInfo}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Degree & Department',
      dataIndex: 'degreeDepartment',
      key: 'degreeDepartment',
      width: '16%',
      render: (_, record) => (
        <div>
          <div>{record.degree}</div>
       
         
            <div >
              {record.department}
            </div>
          
        </div>
      ),
    },
    {
      title: 'Comm. Assmt.',
      dataIndex: 'assessmentCefr',
      key: 'assessmentCefr',
      width: '16%',
      render: (_, record) => (
        <div>
          <div
          style={{ fontWeight: 'bold' }}
          >{record.assessmentCefr}</div>

        </div>
      ),
    },
    {
      title: 'Cognitive Aptitude',
      dataIndex: 'aptitudeCompetency',
      key: 'aptitudeCompetency',
      width: '16%',
      render: (_, record) => (
        <div>
          <div
          style={{ fontWeight: 'bold' }}
          >{record?.aptitudeCompetency}</div>

        </div>
      ),
    },
  ]
  const handleReportDrawerClose = () => {
    setStudentForReport(null)
  }



  return (
    <>
      <div style={{ marginTop: '20px' }}>
        <TableTop>
          <SearchSection>
            <Input
              placeholder="Search by keyword"
              prefix={<SearchOutlined style={{ color: COLORS.GRAY_LIGHT }} />}
              value={studentSearch}
              onChange={handleSearch}
            />
          </SearchSection>

          {/* <FilterContainer>
            <FilterButtonComponent onClick={() => setShowFilters(!showFilters)}>
              <FilterIcon />
              <span>Filter</span>
              <ArrowDropDown />
            </FilterButtonComponent>
            {showFilters && (
              <>
                <FilterPopup></FilterPopup>
                <Overlay onClick={() => setShowFilters(false)} />
              </>
            )}
          </FilterContainer> */}

          <ShowingText>
            Showing {Math.min(students?.length + 10 * (studentPage - 1))} of {totalItems}
          </ShowingText>

    
        </TableTop>

        <div style={{ margin: '10px' }}> </div>

      
        <Spin spinning={loading}>
          {students && students.length > 0 ? (
            <CustomStyledTable
              columns={baseColumns}
              dataSource={paginatedStudents}
              pagination={false}
              rowKey="id"
           
              size="middle"
              bordered={false}
              rowClassName={() => 'table-row'}
            />
          ) : (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <Empty description="No student data available" />
            </div>
          )}
        </Spin>


        {totalItems > 10 && (
          <div
            style={{
              margin: '16px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Pagination
              current={studentPage}
              total={totalItems}
              pageSize={parseInt(10)}
              onChange={handlePageChange}
          
              size="default"
              showSizeChanger={false}
            />
          </div>
        )}
      </div>

      {/* New Student Report Modal */}
      {studentForReport && (
        <NewStudentReport
          visible={!!studentForReport}
          onClose={handleReportDrawerClose}
          student={studentForReport}
          onBack={handleReportDrawerClose}
        />
      )}
    </>
  )
}

export default StudentsTable
